import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Drawer,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import OAuthScopeEndpointsDrawer from './OAuthScopeEndpointsDrawer';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateGridActionIconSx,
  harmonateSectionTitleSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const SCOPES_URL = `${getBaseUrl()}conductor/oauth-scopes`;
const SCOPES_PUBLIC_URL = `${getBaseUrl()}oauth2/scopes`;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const parseJsonSafely = async (res) => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const emptyScopeForm = () => ({
  scopeName: '',
  displayName: '',
  resourceKey: '',
  action: 'read',
  description: '',
  sortOrder: '0',
});

const OAuthScopes = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.EXTERNAL_INTEGRATION);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedScopeName, setSelectedScopeName] = useState(null);
  const [endpointsDrawerOpen, setEndpointsDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [scopeForm, setScopeForm] = useState(emptyScopeForm());

  const selectedScope = useMemo(
    () => rows.find((r) => r.id === selectedScopeName) || null,
    [rows, selectedScopeName]
  );

  const loadScopes = async () => {
    setLoading(true);
    try {
      let res = await fetch(SCOPES_URL, { headers: authHeaders() });
      if (!res.ok) {
        res = await fetch(SCOPES_PUBLIC_URL, { headers: { Accept: 'application/json' } });
      }
      if (!res.ok) throw new Error('Failed to load scopes');
      const data = await parseJsonSafely(res);
      const list = Array.isArray(data) ? data : [];
      setRows(list.map((scope) => ({ id: scope.scope_name, ...scope })));
    } catch (e) {
      toast.error(e.message || 'Failed to load scopes');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScopes();
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'scope_name',
        headerName: 'Scope',
        width: 160,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {params.value}
          </Typography>
        ),
      },
      { field: 'display_name', headerName: 'Display Name', width: 210 },
      { field: 'resource_key', headerName: 'Resource', width: 110 },
      { field: 'action', headerName: 'Action', width: 100 },
      { field: 'description', headerName: 'Description', flex: 1, minWidth: 220 },
    ],
    []
  );

  const openCreate = () => {
    if (!canAdd) return;
    setIsEdit(false);
    setScopeForm(emptyScopeForm());
    setFormOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedScope) return;
    setIsEdit(true);
    setScopeForm({
      scopeName: selectedScope.scope_name,
      displayName: selectedScope.display_name || '',
      resourceKey: selectedScope.resource_key || '',
      action: selectedScope.action || 'read',
      description: selectedScope.description || '',
      sortOrder: String(selectedScope.sort_order ?? 0),
    });
    setFormOpen(true);
  };

  const saveScope = async () => {
    if ((isEdit && !canEdit) || (!isEdit && !canAdd)) return;
    try {
      if (!scopeForm.displayName.trim() || !scopeForm.resourceKey.trim() || !scopeForm.action.trim()) {
        toast.error('Display name, resource key, and action are required');
        return;
      }
      setSubmitting(true);
      const payload = {
        displayName: scopeForm.displayName.trim(),
        resourceKey: scopeForm.resourceKey.trim(),
        action: scopeForm.action.trim(),
        description: scopeForm.description.trim(),
        sortOrder: scopeForm.sortOrder,
      };
      let res;
      if (isEdit) {
        res = await fetch(`${SCOPES_URL}/${encodeURIComponent(selectedScopeName)}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(payload),
        });
      } else {
        if (!scopeForm.scopeName.trim()) {
          toast.error('Scope name is required (e.g. conductor:read)');
          setSubmitting(false);
          return;
        }
        res = await fetch(SCOPES_URL, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            ...payload,
            scopeName: scopeForm.scopeName.trim(),
          }),
        });
      }
      const body = await parseJsonSafely(res);
      if (!res.ok) throw new Error(body?.error || 'Failed to save scope');
      toast.success(isEdit ? 'Scope updated' : 'Scope created');
      setFormOpen(false);
      await loadScopes();
    } catch (e) {
      toast.error(e.message || 'Failed to save scope');
    } finally {
      setSubmitting(false);
    }
  };

  const openDelete = () => {
    if (!canDelete || !selectedScopeName) return;
    setDeleteOpen(true);
  };

  const handleDeleteScope = async () => {
    if (!canDelete || !selectedScopeName) return;
    try {
      setDeleteLoading(true);
      const res = await fetch(`${SCOPES_URL}/${encodeURIComponent(selectedScopeName)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const body = await parseJsonSafely(res);
      if (!res.ok) throw new Error(body?.error || 'Failed to delete scope');
      setDeleteOpen(false);
      toast.success('Scope deleted');
      setSelectedScopeName(null);
      setEndpointsDrawerOpen(false);
      await loadScopes();
    } catch (e) {
      toast.error(e.message || 'Failed to delete scope');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Paper
      sx={{
        p: 2,
        flex: 1,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        bgcolor: 'transparent',
        boxShadow: 'none',
      }}
      elevation={0}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexShrink: 0 }}>
        <Box>
          <Typography variant="h6" sx={harmonateSectionTitleSx}>
            OAuth Scopes
          </Typography>
          <Typography variant="body2" sx={{ color: ht.text }}>
            Global scope catalog used by Conductor API clients. Double-click a row to manage endpoints.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={canAdd ? 'Add' : 'No add permission'}>
            <span>
              <IconButton size="small" onClick={openCreate} disabled={!canAdd} sx={harmonateGridActionIconSx}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Edit' : 'No edit permission'}>
            <span>
              <IconButton size="small" onClick={openEdit} disabled={!selectedScopeName || !canEdit} sx={harmonateGridActionIconSx}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete' : 'No delete permission'}>
            <span>
              <IconButton size="small" onClick={openDelete} disabled={!selectedScopeName || !canDelete} sx={harmonateGridActionIconSx}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 1.5, flexShrink: 0, borderColor: ht.blue, color: ht.text }}>
        Scopes are global. Endpoint path patterns should match gateway paths
        (e.g. <code>/conductor/**</code>).
      </Alert>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          density="standard"
          columnHeaderHeight={48}
          rowHeight={40}
          pagination
          checkboxSelection={false}
          disableRowSelectionOnClick={false}
          onRowClick={(params) => setSelectedScopeName(params.row.id)}
          onCellDoubleClick={(params) => {
            setSelectedScopeName(params.row.id);
            setEndpointsDrawerOpen(true);
          }}
          rowSelectionModel={selectedScopeName ? [selectedScopeName] : []}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            width: '100%',
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Box>

      <Drawer
        anchor="right"
        open={endpointsDrawerOpen}
        onClose={() => setEndpointsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 520 },
            bgcolor: ht.drawerBg,
          },
        }}
      >
        <OAuthScopeEndpointsDrawer
          open={endpointsDrawerOpen}
          onClose={() => setEndpointsDrawerOpen(false)}
          scope={selectedScope}
        />
      </Drawer>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm" PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{isEdit ? 'Edit Scope' : 'Add Scope'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            label="Scope name"
            size="small"
            disabled={isEdit}
            placeholder="conductor:read"
            value={scopeForm.scopeName}
            onChange={(e) => setScopeForm((p) => ({ ...p, scopeName: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Display name"
            size="small"
            value={scopeForm.displayName}
            onChange={(e) => setScopeForm((p) => ({ ...p, displayName: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Resource key"
            size="small"
            placeholder="conductor"
            value={scopeForm.resourceKey}
            onChange={(e) => setScopeForm((p) => ({ ...p, resourceKey: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            select
            label="Action"
            size="small"
            value={scopeForm.action}
            onChange={(e) => setScopeForm((p) => ({ ...p, action: e.target.value }))}
            fullWidth
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            <MenuItem value="read">read</MenuItem>
            <MenuItem value="write">write</MenuItem>
            <MenuItem value="admin">admin</MenuItem>
          </TextField>
          <TextField
            label="Description"
            size="small"
            value={scopeForm.description}
            onChange={(e) => setScopeForm((p) => ({ ...p, description: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Sort order"
            size="small"
            type="number"
            value={scopeForm.sortOrder}
            onChange={(e) => setScopeForm((p) => ({ ...p, sortOrder: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setFormOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" disabled={submitting} onClick={saveScope} sx={harmonateProceedButtonSx}>
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleteLoading && setDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete scope &quot;{selectedScopeName}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteScope}
            variant="contained"
            disabled={deleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OAuthScopes;
